// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/holiday/components/HolidayFlybyFlock.module.css';
import batFlapUrl from '@app/features/holiday/packs/halloween/assets/flyby/bat-flap.svg';
import Holiday, {type HolidayBatFlockParticle} from '@app/features/holiday/state/Holiday';
import type React from 'react';
import {useEffect} from 'react';

const DESPAWN_SLACK_MS = 200;

function flockStyle(particle: HolidayBatFlockParticle): React.CSSProperties {
	return {
		'--flyby-duration': `${particle.durationMs}ms`,
		'--flyby-start-delay': `${particle.startDelayMs ?? 0}ms`,
		'--flyby-y-from': `${(particle.bandFrom * 100).toFixed(2)}%`,
		'--flyby-y-to': `${(particle.bandTo * 100).toFixed(2)}%`,
		'--flyby-scale-from': String(particle.scaleFrom),
		'--flyby-scale-to': String(particle.scaleTo),
		'--flyby-opacity-from': String(particle.opacityFrom),
		'--flyby-opacity-to': String(particle.opacityTo),
		'--bat-flap': `url("${batFlapUrl}")`,
		'--bat-flap-duration': `${particle.flapDurationMs}ms`,
		...(particle.path === 'cross'
			? {
					'--flyby-wave-count': String(particle.waveCount ?? 0.9),
					'--flyby-wave-amp-frac': String(particle.waveAmpFrac ?? 0.02),
					'--flyby-wave-phase': String(particle.wavePhase ?? 0),
					'--flyby-drift': String(particle.bandTo - particle.bandFrom),
					'--flyby-tilt-ref': String(particle.tiltRef ?? 0.2),
				}
			: {}),
		...(particle.path === 'rise'
			? {
					'--flyby-origin-x': `${particle.originX ?? 0}px`,
					'--flyby-origin-y': `${particle.originY ?? 0}px`,
				}
			: {}),
	} as React.CSSProperties;
}

function batStyle(bat: HolidayBatFlockParticle['bats'][number]): React.CSSProperties {
	return {
		'--bat-size': `${bat.sizeRem}rem`,
		'--bat-x': `${bat.offsetXRem}rem`,
		'--bat-y': `${bat.offsetYRem}rem`,
		'--bat-opacity': String(bat.opacity),
		'--bat-flap-delay': `${bat.flapDelayMs}ms`,
		...(bat.flapDurationMs != null ? {'--bat-flap-duration': `${bat.flapDurationMs}ms`} : {}),
		...(bat.waveLag != null ? {'--bat-wave-lag': String(bat.waveLag)} : {}),
		...(bat.flapCycles != null ? {'--flap-cycles': String(bat.flapCycles)} : {}),
		...(bat.flapFm != null ? {'--flap-fm': String(bat.flapFm)} : {}),
		...(bat.flapOffset != null ? {'--bat-flap-offset': String(bat.flapOffset)} : {}),
		...(bat.helixPath != null ? {'--bat-helix-path': `path("${bat.helixPath}")`} : {}),
	} as React.CSSProperties;
}

export function HolidayFlybyFlock({particle}: {particle: HolidayBatFlockParticle}): React.ReactElement {
	useEffect(() => {
		const timeoutId = window.setTimeout(
			() => {
				Holiday.removeParticle(particle.id);
			},
			particle.durationMs + (particle.startDelayMs ?? 0) + DESPAWN_SLACK_MS,
		);
		const onVisibility = (): void => {
			if (document.hidden) Holiday.removeParticle(particle.id);
		};
		document.addEventListener('visibilitychange', onVisibility);
		return () => {
			window.clearTimeout(timeoutId);
			document.removeEventListener('visibilitychange', onVisibility);
		};
	}, [particle.id, particle.durationMs, particle.startDelayMs]);

	return (
		<div
			className={`${styles.flock} ${styles.motion}`}
			style={flockStyle(particle)}
			data-direction={particle.direction}
			data-path={particle.path}
			data-flx="holiday.holiday-flyby-flock"
			aria-hidden="true"
		>
			<div className={styles.formation} data-flx="holiday.holiday-flyby-flock.formation">
				{particle.bats.map((bat, index) => (
					<div key={index} className={styles.bat} style={batStyle(bat)} data-flx="holiday.holiday-flyby-flock.bat">
						{bat.helixPath != null ? (
							<div className={styles.orbit} data-flx="holiday.holiday-flyby-flock.orbit">
								<div className={styles.sprite} data-flx="holiday.holiday-flyby-flock.sprite" />
							</div>
						) : (
							<div className={styles.sprite} data-flx="holiday.holiday-flyby-flock.sprite--2" />
						)}
					</div>
				))}
			</div>
		</div>
	);
}
